import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge } from '@mono/ui-components';
import { Plane } from 'lucide-react';
import { memo, useCallback } from 'react';
import { ParameterCell } from './ParameterCell';
function formatCurrency(value) {
    if (value === 0) return '$0';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${value.toLocaleString(undefined, {
        maximumFractionDigits: 0
    })}`;
    return `$${value.toFixed(2)}`;
}
export const TravelClinEditor = /*#__PURE__*/ memo(function TravelClinEditor({ clinPricing, onChange }) {
    const travelConfig = clinPricing.metadata.travelConfig;
    const handleHandlingRateChange = useCallback((value)=>{
        if (!travelConfig) return;
        const updatedParam = {
            ...travelConfig.handlingRatePct,
            value
        };
        onChange({
            ...clinPricing,
            metadata: {
                ...clinPricing.metadata,
                travelConfig: {
                    ...travelConfig,
                    handlingRatePct: updatedParam
                }
            }
        });
    }, [
        clinPricing,
        travelConfig,
        onChange
    ]);
    if (!travelConfig) return null;
    const handlingPct = travelConfig.handlingRatePct.value ?? 0;
    const computedTotal = travelConfig.governmentNTE * (1 + handlingPct);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-3",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "flex items-center gap-2 flex-wrap",
                children: [
                    /*#__PURE__*/ _jsxs(Badge, {
                        variant: "outline",
                        className: "text-xs bg-green-50 text-green-700 border-green-200",
                        children: [
                            /*#__PURE__*/ _jsx(Plane, {
                                className: "w-3 h-3 mr-1 inline"
                            }),
                            "Travel"
                        ]
                    }),
                    clinPricing.metadata.periodName && /*#__PURE__*/ _jsx(Badge, {
                        variant: "outline",
                        className: "text-xs bg-blue-50 text-blue-700 border-blue-200",
                        children: clinPricing.metadata.periodName
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: "bg-white border border-gray-200 rounded-lg divide-y divide-gray-100",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between px-4 py-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-sm font-medium text-gray-700",
                                        children: "Government NTE"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-xs text-gray-400",
                                        children: "Not-to-exceed ceiling (for evaluation)"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm font-semibold text-violet-700 bg-violet-50 px-3 py-1 rounded",
                                children: formatCurrency(travelConfig.governmentNTE)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between px-4 py-3",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-sm font-medium text-gray-700",
                                        children: "Fixed Handling Rate %"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-xs text-gray-400",
                                        children: "Applied to travel costs"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "w-32",
                                children: /*#__PURE__*/ _jsx(ParameterCell, {
                                    param: travelConfig.handlingRatePct,
                                    editable: true,
                                    onChange: handleHandlingRateChange
                                })
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "flex items-center justify-between px-4 py-3 bg-gray-50",
                        children: [
                            /*#__PURE__*/ _jsxs("div", {
                                children: [
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-sm font-medium text-gray-700",
                                        children: "Computed Total"
                                    }),
                                    /*#__PURE__*/ _jsx("div", {
                                        className: "text-xs text-gray-400",
                                        children: "NTE × (1 + handling rate)"
                                    })
                                ]
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm font-semibold text-gray-900",
                                children: formatCurrency(computedTotal)
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
